import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateHostDetails } from '../../store/thunks/HostActions';
import editIcon from '../../icons/Edit.svg';

function EntryInstructionsEmailSettings({ host, setSuccessMessage, setErrorMessage }) {
  const dispatch = useDispatch();
  const { hid } = useParams();
  const [editing, setEditing] = useState(false);
  const [receiveEmails, setReceiveEmails] = useState(host?.receiveEntryInstructionsEmails ?? true);
  const [additionalEmails, setAdditionalEmails] = useState(host?.entryInstructionsAdditionalEmails || []);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggestedEmails, setSuggestedEmails] = useState([]);

  useEffect(() => {
    if (host) {
      setReceiveEmails(host.receiveEntryInstructionsEmails ?? true);
      setAdditionalEmails(host.entryInstructionsAdditionalEmails || []);

      // Extract email suggestions from internal notes
      const notes = host.internalHostNotes || '';
      const regex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
      const found = notes.match(regex) || [];
      const cleaned = Array.from(new Set(found.map((e) => e.trim().toLowerCase())));
      setSuggestedEmails(cleaned.filter((e) => !(host.entryInstructionsAdditionalEmails || []).includes(e)));
    }
  }, [host]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim().toLowerCase();
    
    if (!trimmedEmail) {
      setErrorMessage('Please enter an email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (additionalEmails.includes(trimmedEmail)) {
      setErrorMessage('This email is already in the list');
      return;
    }

    setAdditionalEmails([...additionalEmails, trimmedEmail]);
    setNewEmail('');
    setErrorMessage(null);
  };

  const handleRemoveEmail = (emailToRemove) => {
    setAdditionalEmails(additionalEmails.filter(email => email !== emailToRemove));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage(null);

      const response = await dispatch(updateHostDetails({
        hid,
        profileCredentials: {
          receiveEntryInstructionsEmails: receiveEmails,
          entryInstructionsAdditionalEmails: additionalEmails,
        }
      }));

      if (updateHostDetails.fulfilled.match(response)) {
        setSuccessMessage('Email settings saved successfully');
        setEditing(false);
        // Refresh host details
        window.location.reload();
      } else {
        const errorMsg = response.payload?.error || response.payload?.message || 'Failed to save email settings';
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setErrorMessage('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-[20px] font-bold text-gray-900">Entry Instructions Email Settings</h2>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="p-1 hover:opacity-80"
          >
            <img src={editIcon} alt="edit" className="w-4 h-4" />
          </button>
        )}
        {editing && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                // Reset to original values
                setReceiveEmails(host?.receiveEntryInstructionsEmails ?? true);
                setAdditionalEmails(host?.entryInstructionsAdditionalEmails || []);
                setNewEmail('');
                setErrorMessage(null);
              }}
              className="px-3 py-1 text-sm font-semibold rounded bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1 text-sm font-semibold rounded bg-[#006aff] text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Checkbox for receiving emails */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="receiveEmails"
            checked={receiveEmails}
            onChange={(e) => setReceiveEmails(e.target.checked)}
            disabled={!editing}
            className="h-4 w-4 rounded border-gray-300 text-[#006aff] focus:ring-[#006aff] disabled:opacity-50"
          />
          <label htmlFor="receiveEmails" className="text-sm font-medium text-gray-700">
            Send Entry Instructions Emails
          </label>
        </div>
        <p className="text-xs text-gray-500 ml-7">
          When enabled, this host will receive automated emails requesting entry instructions for new orders.
        </p>

        {/* Additional Emails Section */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Email Recipients
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Add additional email addresses (e.g., team members) who should also receive entry instructions emails.
          </p>

          {/* Email List */}
          {additionalEmails.length > 0 && (
            <div className="mb-3 space-y-2">
              {additionalEmails.map((email, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg"
                >
                  <span className="text-sm text-gray-700">{email}</span>
                  {editing && (
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Add Email Input */}
          {editing && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  placeholder="Enter email address"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#006aff] focus:border-[#006aff] text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddEmail}
                  className="px-4 py-2 bg-[#006aff] text-white rounded-lg hover:opacity-90 text-sm font-medium"
                >
                  Add
                </button>
              </div>

              {/* Suggestions from internal notes */}
              {suggestedEmails.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">Suggested from notes:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedEmails.map((email) => (
                      <button
                        key={email}
                        type="button"
                        onClick={() => {
                          setNewEmail(email);
                          handleAddEmail();
                          setSuggestedEmails(suggestedEmails.filter((e) => e !== email));
                        }}
                        className="px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-800 border border-gray-200 hover:bg-gray-200"
                      >
                        {email}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EntryInstructionsEmailSettings;

